import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem('auth_user');
  store.dispatch(signOutSuccess());
};

service.interceptors.request.use(config => {
  const jwtToken = localStorage.getItem(AUTH_TOKEN);
  if (jwtToken) {
    config.headers['Authorization'] = `Bearer ${jwtToken}`;
  }
  return config;
}, error => {
  notification.error({ message: 'Error' });
  return Promise.reject(error);
});

service.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      notification.error({ message: 'Network Error', description: 'Please check your internet connection' });
      return Promise.reject(error);
    }

    const status = error.response.status;

    if (status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);

      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return service(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        const newToken = data.token || data.accessToken;
        const newRefreshToken = data.refreshToken;

        if (newToken) {
          localStorage.setItem(AUTH_TOKEN, newToken);
          if (newRefreshToken) localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
          if (data.user) localStorage.setItem('auth_user', JSON.stringify(data.user));

          service.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return service(originalRequest);
        }
        processQueue(error);
        forceLogout();
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(refreshError);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    let notificationParam = { message: '' };

    if (status === 400) {
      notificationParam.message = 'Bad Request';
      notificationParam.description = error.response?.data?.message || 'Invalid request data';
    }
    if (status === 403) {
      notificationParam.message = 'Forbidden';
      notificationParam.description = 'You do not have permission';
    }
    if (status === 404) notificationParam.message = 'Not Found';
    if (status === 500) notificationParam.message = 'Internal Server Error';
    if (status === 508) notificationParam.message = 'Time Out';

    if (notificationParam.message) notification.error(notificationParam);

    return Promise.reject(error);
  }
);

export default service;
